import { isAxiosError } from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { apiClientVersioned, returnData } from '../index'

import type { IChatSurvey } from '../types/chatSurvey'

const CHAT_SURVEY_CONFIG_PATH = '/config/chat_survey'

interface IChatSurveyConfigRow {
  value: string
}

export const CHAT_SURVEY_DEFAULT_IDLE_SECONDS = 30
export const CHAT_SURVEY_MIN_OPTIONS = 2
export const CHAT_SURVEY_DEFAULT_MIN_REPLIES = 2
export const CHAT_SURVEY_DEFAULT_MAX_DISMISSALS = 3
export const CHAT_SURVEY_DEFAULT_CHAT_COOLDOWN_HOURS = 24
export const CHAT_SURVEY_NAME_MAX_LENGTH = 80
export const CHAT_SURVEY_KEY_MAX_LENGTH = 64

/** A row saved before there were several surveys holds one survey without these. */
const LEGACY_SURVEY_IDENTITY = {
  id: 'chat-survey',
  key: 'chat_survey',
  name: 'Chat survey',
}

type TStoredChatSurvey = Partial<Record<keyof IChatSurvey, unknown>>

const wholeNumberOr = (value: unknown, fallback: number, minimum: number) =>
  Number.isInteger(value) && (value as number) >= minimum
    ? (value as number)
    : fallback

/** The config row holds any string, so anything malformed reads as "no survey". */
const parseChatSurvey = (stored: unknown): IChatSurvey | null => {
  const survey = (stored ?? {}) as TStoredChatSurvey
  const isValid =
    typeof survey.id === 'string' &&
    typeof survey.key === 'string' &&
    typeof survey.name === 'string' &&
    typeof survey.is_live === 'boolean' &&
    (survey.published_at === null || typeof survey.published_at === 'string') &&
    typeof survey.question === 'string' &&
    Array.isArray(survey.options) &&
    survey.options.length >= CHAT_SURVEY_MIN_OPTIONS &&
    survey.options.every(
      (option: unknown) =>
        typeof (option as IChatSurvey['options'][number])?.id === 'string' &&
        typeof (option as IChatSurvey['options'][number])?.label === 'string',
    )
  if (!isValid) return null
  // Rows saved before these were editable don't have them.
  return {
    ...(survey as IChatSurvey),
    idle_seconds:
      typeof survey.idle_seconds === 'number' && survey.idle_seconds > 0
        ? survey.idle_seconds
        : CHAT_SURVEY_DEFAULT_IDLE_SECONDS,
    is_dismissable: survey.is_dismissable !== false,
    display_frequency:
      survey.display_frequency === 'once_per_chat'
        ? 'once_per_chat'
        : 'once_per_session',
    is_reading_time_added: survey.is_reading_time_added !== false,
    min_replies: wholeNumberOr(
      survey.min_replies,
      CHAT_SURVEY_DEFAULT_MIN_REPLIES,
      1,
    ),
    max_dismissals: wholeNumberOr(
      survey.max_dismissals,
      CHAT_SURVEY_DEFAULT_MAX_DISMISSALS,
      0,
    ),
    chat_cooldown_hours: wholeNumberOr(
      survey.chat_cooldown_hours,
      CHAT_SURVEY_DEFAULT_CHAT_COOLDOWN_HOURS,
      0,
    ),
  }
}

/** The config row as this build reads it. */
interface IChatSurveyRow {
  surveys: IChatSurvey[]
  /** Entries that don't parse, written back untouched so a change never drops them. */
  unreadableSurveys: unknown[]
  /** Keys of deleted surveys, never given out again so their PostHog results stay apart. */
  retiredKeys: string[]
}

const createEmptyRow = (): IChatSurveyRow => ({
  surveys: [],
  unreadableSurveys: [],
  retiredKeys: [],
})

const parseChatSurveyRow = (value: string): IChatSurveyRow => {
  try {
    const row = JSON.parse(value)
    if (!Array.isArray(row?.surveys)) {
      const legacySurvey = parseChatSurvey({
        ...LEGACY_SURVEY_IDENTITY,
        ...row,
      })
      return {
        ...createEmptyRow(),
        surveys: legacySurvey ? [legacySurvey] : [],
      }
    }
    const parsedRow = createEmptyRow()
    row.surveys.forEach((stored: unknown) => {
      const survey = parseChatSurvey(stored)
      if (survey) parsedRow.surveys.push(survey)
      else parsedRow.unreadableSurveys.push(stored)
    })
    if (Array.isArray(row.retired_keys)) {
      parsedRow.retiredKeys = row.retired_keys.filter(
        (key: unknown) => typeof key === 'string',
      )
    }
    return parsedRow
  } catch {
    return createEmptyRow()
  }
}

/** Publishing unpublishes the others, so more than one live only comes from a hand edit. */
const findLiveSurvey = (surveys: IChatSurvey[]): IChatSurvey | null =>
  surveys
    .filter((survey) => survey.is_live && survey.published_at)
    .sort((a, b) =>
      `${b.published_at}`.localeCompare(`${a.published_at}`),
    )[0] ?? null

const findTakenKeys = ({
  surveys,
  unreadableSurveys,
  retiredKeys,
}: IChatSurveyRow) =>
  new Set<unknown>([
    ...surveys.map(({ key }) => key),
    ...unreadableSurveys.map((stored) => (stored as TStoredChatSurvey)?.key),
    ...retiredKeys,
  ])

/** Lowercase words joined with `_`, numbered when the key is taken, even by a deleted survey. */
const createSurveyKey = (name: string, takenKeys: Set<unknown>) => {
  const base =
    name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, CHAT_SURVEY_KEY_MAX_LENGTH - 3) || 'survey'
  let key = base
  for (let suffix = 2; takenKeys.has(key); suffix += 1) {
    key = `${base}_${suffix}`
  }
  return key
}

const createSurvey = (name: string, takenKeys: Set<unknown>): IChatSurvey => ({
  id: uuidv4(),
  key: createSurveyKey(name, takenKeys),
  name,
  is_live: false,
  published_at: null,
  question: '',
  options: [
    { id: uuidv4(), label: '' },
    { id: uuidv4(), label: '' },
  ],
  idle_seconds: CHAT_SURVEY_DEFAULT_IDLE_SECONDS,
  is_dismissable: true,
  display_frequency: 'once_per_session',
  is_reading_time_added: true,
  min_replies: CHAT_SURVEY_DEFAULT_MIN_REPLIES,
  max_dismissals: CHAT_SURVEY_DEFAULT_MAX_DISMISSALS,
  chat_cooldown_hours: CHAT_SURVEY_DEFAULT_CHAT_COOLDOWN_HOURS,
})

/** Readable by any signed-in user. Empty until an admin first saves one (404). */
const readSurveyRow = async () => {
  try {
    const { value } = await returnData(
      apiClientVersioned.get<IChatSurveyConfigRow>(CHAT_SURVEY_CONFIG_PATH),
    )
    return parseChatSurveyRow(value)
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return createEmptyRow()
    }
    throw error
  }
}

const listSurveys = async () => (await readSurveyRow()).surveys

/**
 * Admin only. The row is replaced whole, so each change re-reads it first and
 * only overwrites the surveys it touches.
 */
const changeSurveyRow = async (
  change: (_row: IChatSurveyRow) => IChatSurveyRow,
) => {
  const row = change(await readSurveyRow())
  await returnData(
    apiClientVersioned.post<IChatSurveyConfigRow>(CHAT_SURVEY_CONFIG_PATH, {
      value: JSON.stringify({
        surveys: [...row.surveys, ...row.unreadableSurveys],
        retired_keys: row.retiredKeys,
      }),
    }),
  )
  return row.surveys
}

const changeSurveys = (change: (_surveys: IChatSurvey[]) => IChatSurvey[]) =>
  changeSurveyRow((row) => ({ ...row, surveys: change(row.surveys) }))

/** A survey deleted meanwhile must fail the change, not quietly save nothing. */
const assertSurveyExists = (surveys: IChatSurvey[], id: string) => {
  if (!surveys.some((survey) => survey.id === id)) {
    throw new Error(
      'This survey no longer exists. Someone may have deleted it.',
    )
  }
}

export const ChatSurveyApi = {
  /** The survey users are asked, or `null` when none is live. */
  get: async () => findLiveSurvey(await listSurveys()),
  list: listSurveys,
  /** Adds a draft with a key made from its name. Newest first. */
  create: async (name: string) => {
    let created: IChatSurvey | undefined
    const surveys = await changeSurveyRow((row) => {
      created = createSurvey(name, findTakenKeys(row))
      return { ...row, surveys: [created, ...row.surveys] }
    })
    return { created: created as IChatSurvey, surveys }
  },
  /** Keeps the stored live state, so a save can't undo a publish made meanwhile. */
  update: (survey: IChatSurvey) =>
    changeSurveys((surveys) => {
      assertSurveyExists(surveys, survey.id)
      return surveys.map((current) =>
        current.id === survey.id
          ? {
              ...survey,
              is_live: current.is_live,
              published_at: current.published_at,
            }
          : current,
      )
    }),
  /** Unpublishes every other survey, so only one is ever live. */
  publish: (survey: IChatSurvey) => {
    const publishedAt = new Date().toISOString()
    return changeSurveys((surveys) => {
      assertSurveyExists(surveys, survey.id)
      return surveys.map((current) =>
        current.id === survey.id
          ? { ...survey, is_live: true, published_at: publishedAt }
          : { ...current, is_live: false },
      )
    })
  },
  unpublish: (id: string) =>
    changeSurveys((surveys) =>
      surveys.map((current) =>
        current.id === id ? { ...current, is_live: false } : current,
      ),
    ),
  /** Retires the key, so a later survey with the same name gets a new one. */
  delete: (id: string) =>
    changeSurveyRow((row) => {
      const deleted = row.surveys.find((survey) => survey.id === id)
      return {
        ...row,
        surveys: row.surveys.filter((survey) => survey.id !== id),
        retiredKeys: deleted
          ? [...row.retiredKeys, deleted.key]
          : row.retiredKeys,
      }
    }),
}
